import { Button, Spin, Table } from 'antd';
import { TableRowSelection } from 'antd/lib/table/interface';
import React, { FC, useEffect, useState } from 'react';
import { batch } from 'react-redux';
import { ColumnsProps, buildColumn, handleResponse, parseDateTimeString } from '../../../Helper';
import { eventApi } from '../../../api';
import { AcknowledgedAndClearAlarmsPayload, AlarmAcknowledgementActions, AlarmModel, AlarmNoteForAcknowledge } from '../../../model/AlarmModel';
import { ActionGraphicContextMenu, VelocityObject } from '../../../model/GraphicModel';
import { useStoreDispatch } from '../../../store';
import { setAlarmNoteForAcknowledge, setShowAlarmAddOperatorNote } from '../../../store/alarm/actions';
import { acknowledgeAndClearAlarmCommon, acknowledgedAndClearAlarms, getAlarmNoteAddOperatorNoteModal, getMultipleAlarmAction } from '../../alarms/helpers';
import { Modal } from '../../common';
import styles from './AlarmTableModal.module.scss';

type AlarmTableModalProps = {
	spaceObjectSelected: Partial<VelocityObject>;
	alarmsAction: ActionGraphicContextMenu;
	setShowModal: (flag: boolean) => void;
};

export type AlarmNoteData = {
	alarmIds: number[];
	action: AlarmAcknowledgementActions;
	showModal: boolean;
};

const FIRST_ALARM_INDEX = 0;

const AlarmTableModal: FC<AlarmTableModalProps> = ({ spaceObjectSelected, alarmsAction, setShowModal }) => {
	const dispatch = useStoreDispatch();

	const [activeAlarmsSelected, setActiveAlarmsSelected] = useState<string[]>([]);
	const [activeAlarms, setActiveAlarms] = useState<AlarmModel[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const isAcknowledgedAlarm = alarmsAction === ActionGraphicContextMenu.CLEAR_ALARMS;

	useEffect(() => {
		getActiveAlarms();
	}, []);

	const handleOnClickSelectAll = () => {
		setActiveAlarmsSelected(getTotalActiveAlarmIds());
	};

	const handleOnClickOk = () => {
		let alarmAction = getAlarmOptionSelected(alarmsAction);

		let payload: AcknowledgedAndClearAlarmsPayload = {
			action: alarmAction,
			operatorNotes: [],
			alarmIds: activeAlarmsSelected.map<number>(x => Number(x)),
			acknowledgeAndClearCallback: acknowledgeAndClearAlarmCommon,
			addOperatorNoteCallback,
			setAlarmNoteForAcknowledgeCallback,
			setShowAlarmAddOperatorNoteCallback,
			saveChangesCallback: closeModal,
		};

		switch (alarmAction) {
			case AlarmAcknowledgementActions.AcknowledgedOne:
			case AlarmAcknowledgementActions.ClearOneAcknowledged:
			case AlarmAcknowledgementActions.ClearOneUnacknowledged:
				payload.action = getMultipleAlarmAction(activeAlarmsSelected, alarmAction);
				break;

			case AlarmAcknowledgementActions.AcknowledgedAll:
			case AlarmAcknowledgementActions.ClearAllAcknowledged:
			case AlarmAcknowledgementActions.ClearAllUnacknowledged:
				payload.alarmsDetails = activeAlarms;
				break;
		}

		acknowledgedAndClearAlarms(payload);
	};

	const getTotalActiveAlarmIds = () => {
		return activeAlarms.map(x => getActiveAlarmId(x));
	};

	const getAlarmOptionSelected = (option: ActionGraphicContextMenu): AlarmAcknowledgementActions => {
		const totalActiveAlarms = getTotalActiveAlarmIds();

		const isAcknowledgedAlarm = option === ActionGraphicContextMenu.ACKNOWLEDGED_ALARMS;

		if (activeAlarmsSelected.length === totalActiveAlarms.length) {
			return isAcknowledgedAlarm ? AlarmAcknowledgementActions.AcknowledgedAll : AlarmAcknowledgementActions.ClearAllAcknowledged;
		}

		if (activeAlarmsSelected.length > 1) {
			return isAcknowledgedAlarm ? AlarmAcknowledgementActions.AcknowledgedMultiple : AlarmAcknowledgementActions.ClearMultipleAcknowledged;
		} else {
			return isAcknowledgedAlarm ? AlarmAcknowledgementActions.AcknowledgedOne : AlarmAcknowledgementActions.ClearOneAcknowledged;
		}
	};

	const setAlarmNoteForAcknowledgeCallback = (alarmNote: AlarmNoteForAcknowledge) => {
		dispatch(setAlarmNoteForAcknowledge(alarmNote));
	};

	const addOperatorNoteCallback = (action: AlarmAcknowledgementActions, payload: AcknowledgedAndClearAlarmsPayload) => {
		return getAlarmNoteAddOperatorNoteModal(action, true, payload.alarmIds, activeAlarms, payload.alarmsDetails);
	};

	const setShowAlarmAddOperatorNoteCallback = () => {
		dispatch(setShowAlarmAddOperatorNote(true));
	};

	const getActiveAlarms = async () => {
		const { Address, ObjectType } = spaceObjectSelected;
		setIsLoading(true);
		const response = await eventApi.retrieveActiveAlarmsByAddress(Address, ObjectType, isAcknowledgedAlarm);

		if (handleResponse(response)) {
			return;
		}

		batch(() => {
			setIsLoading(false);
			setActiveAlarms(buildDataSource(response.Entity));
		});
	};

	const getActiveAlarmId = (alarm: AlarmModel) => {
		if (alarm.AlarmDetails.length) {
			return alarm.AlarmDetails.at(FIRST_ALARM_INDEX)?.ActiveAlarmId?.toString();
		}
	};

	const buildDataSource = (source: AlarmModel[]) => {
		return source.map(x => ({
			...x,
			key: getActiveAlarmId(x),
			HostTime: x.AlarmDetails.at(FIRST_ALARM_INDEX)?.HostTime,
		}));
	};

	const closeModal = () => {
		setShowModal(false);
	};

	const columns: ColumnsProps<AlarmModel>[] = [
		{
			...buildColumn(_('Description'), 'Description', 'auto', 'start'),
		},
		{
			...buildColumn(_('Time'), 'HostTime', '12em', 'start'),
			render: value => parseDateTimeString(value, true, value),
		},
		{
			...buildColumn(_('Level'), 'Level', '4em', 'start'),
		},
		{
			...buildColumn(_('AlarmID'), 'EventId', '6em', 'start'),
		},
	];

	const rowSelection: TableRowSelection<AlarmModel> = {
		preserveSelectedRowKeys: true,
		type: 'checkbox',
		selectedRowKeys: activeAlarmsSelected,
		onChange: (selectedRowKeys: string[]) => {
			setActiveAlarmsSelected(selectedRowKeys);
		},
	};

	return (
		<Modal title={_('AlarmSelection')} width={700} onCancel={() => setShowModal(false)} visible={true}>
			<div id='graphicAlarmTableModalContainer' className={styles.alarmContainer}>
				<Spin tip={`${_('Loading')}...`} spinning={isLoading} size='large'>
					<Table
						id='graphicAlarmTable'
						className={styles.alarmInfoTable}
						columns={columns}
						dataSource={activeAlarms}
						pagination={false}
						rowSelection={rowSelection}
						scroll={{ y: '730px' }}
						size='small'
					/>
				</Spin>
				<div className={styles.buttons}>
					<Button id='graphicAlarmTableModalOkButton' disabled={!activeAlarmsSelected || activeAlarmsSelected.length < 1} onClick={handleOnClickOk}>
						{_('OK')}
					</Button>
					<Button id='graphicAlarmTableModalCancelButton' onClick={closeModal}>
						{_('Cancel')}
					</Button>
					<Button id='graphicAlarmTableModalSelectAllButton' onClick={() => handleOnClickSelectAll()}>
						{_('SelectAll')}
					</Button>
				</div>
			</div>
		</Modal>
	);
};

export { AlarmTableModal };
